import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Surface, TextField, Label, InputGroup, Button, Alert } from '@heroui/react'
import { useAuth } from '../../contexts/AuthContext'
import Mail from '~icons/solar/letter-bold'
import Lock from '~icons/solar/lock-password-bold'
import Eye from '~icons/solar/eye-bold'
import EyeOff from '~icons/solar/eye-closed-bold'
import ArrowLeft from '~icons/solar/arrow-left-outline'
import Check from '~icons/solar/check-circle-bold'
import TrendingUp from '~icons/solar/graph-up-bold'
import LoadingSpinner from '~icons/svg-spinners/270-ring-with-bg'

type Step = 'request' | 'sent' | 'reset' | 'success'

export default function PasswordResetPage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [searchParams] = useSearchParams()
  const resetToken = searchParams.get('token')

  const [step, setStep] = useState<Step>(resetToken ? 'reset' : 'request')
  const [email, setEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email) {
      setError('Please enter your email address')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    setLoading(true)

    try {
      // Always resolves the same way regardless of whether the email
      // matches an account (the endpoint itself never reveals that), so
      // there is nothing meaningful to branch on here beyond a network
      // failure.
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
    } catch {
      // Network failure only. Still move on to the same "check your
      // email" screen, since this request never signals success or
      // failure of the underlying lookup either way.
    }

    setLoading(false)
    setStep('sent')
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-affiliate-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: resetToken, password: newPassword }),
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset your password')
      }

      setStep('success')

      // Signs the affiliate straight in with their new password rather
      // than making them re-enter it on the sign in page right after
      // just choosing it.
      try {
        await login(data.email, newPassword)
        setTimeout(() => navigate('/affiliate/dashboard'), 2000)
      } catch {
        setTimeout(() => navigate('/auth'), 3000)
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-accent rounded-xl mb-4">
            <TrendingUp className="h-8 w-8 text-accent-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {step === 'request' && 'Reset Password'}
            {step === 'sent' && 'Check Your Email'}
            {step === 'reset' && 'Create New Password'}
            {step === 'success' && 'Password Reset!'}
          </h1>
          <p className="text-muted">
            {step === 'request' && 'Enter your email to receive reset instructions'}
            {step === 'sent' && 'We sent you a password reset link'}
            {step === 'reset' && 'Enter your new password below'}
            {step === 'success' && 'Your password has been successfully reset'}
          </p>
        </div>

        {/* Main Card */}
        <Surface variant="default" className="rounded-2xl p-8">
          {/* Request Reset Form */}
          {step === 'request' && (
            <form onSubmit={handleRequestReset} className="space-y-6">
              {error && (
                <Alert status="danger" className="bg-danger-soft">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Description className="text-danger-soft-foreground">{error}</Alert.Description>
                  </Alert.Content>
                </Alert>
              )}

              <TextField isRequired type="email" name="email" value={email} onChange={setEmail}>
                <Label>Email Address</Label>
                <InputGroup variant="secondary">
                  <InputGroup.Prefix>
                    <Mail className="h-4 w-4" />
                  </InputGroup.Prefix>
                  <InputGroup.Input placeholder="you@example.com" />
                </InputGroup>
              </TextField>

              <Button type="submit" className="w-full" isDisabled={loading}>
                {loading ? (
                  <>
                    <LoadingSpinner className="h-4 w-4" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <Button variant="ghost" className="w-full" onPress={() => navigate('/auth')}>
                <ArrowLeft className="h-4 w-4" />
                Back to Sign In
              </Button>
            </form>
          )}

          {/* Email Sent Confirmation */}
          {step === 'sent' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-success-soft rounded-full flex items-center justify-center mx-auto">
                <Mail className="h-8 w-8 text-success-soft-foreground" />
              </div>

              <div>
                <p className="text-muted mb-2">
                  We've sent a password reset link to:
                </p>
                <p className="font-medium text-foreground">{email}</p>
              </div>

              <Surface variant="secondary" className="rounded-xl p-4 text-sm text-left text-foreground">
                <p className="mb-2 font-medium">What to do next:</p>
                <ol className="list-decimal list-inside space-y-1 text-muted">
                  <li>Check your email inbox</li>
                  <li>Click the reset link in the email</li>
                  <li>Create a new password</li>
                </ol>
              </Surface>

              <div className="text-sm text-muted">
                Didn't receive the email? Check your spam folder or{' '}
                <button
                  onClick={() => setStep('request')}
                  className="font-medium text-accent hover:underline"
                >
                  try again
                </button>
              </div>

              <Button variant="ghost" className="w-full" onPress={() => navigate('/auth')}>
                <ArrowLeft className="h-4 w-4" />
                Back to Sign In
              </Button>
            </div>
          )}

          {/* Reset Password Form */}
          {step === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              {error && (
                <Alert status="danger" className="bg-danger-soft">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Description className="text-danger-soft-foreground">{error}</Alert.Description>
                  </Alert.Content>
                </Alert>
              )}

              <TextField
                isRequired
                type={showPassword ? 'text' : 'password'}
                name="newPassword"
                value={newPassword}
                onChange={setNewPassword}
              >
                <Label>New Password</Label>
                <InputGroup variant="secondary">
                  <InputGroup.Prefix>
                    <Lock className="h-4 w-4" />
                  </InputGroup.Prefix>
                  <InputGroup.Input placeholder="Enter new password" />
                  <InputGroup.Suffix>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-muted hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
                <p className="text-xs text-muted mt-1">At least 8 characters</p>
              </TextField>

              <TextField
                isRequired
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={confirmPassword}
                onChange={setConfirmPassword}
              >
                <Label>Confirm New Password</Label>
                <InputGroup variant="secondary">
                  <InputGroup.Prefix>
                    <Lock className="h-4 w-4" />
                  </InputGroup.Prefix>
                  <InputGroup.Input placeholder="Confirm new password" />
                  <InputGroup.Suffix>
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-muted hover:text-foreground transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
              </TextField>

              <Button type="submit" className="w-full" isDisabled={loading}>
                {loading ? (
                  <>
                    <LoadingSpinner className="h-4 w-4" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}

          {/* Success State */}
          {step === 'success' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-success-soft rounded-full flex items-center justify-center mx-auto">
                <Check className="h-8 w-8 text-success-soft-foreground" />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Password Reset Successful!
                </h3>
                <p className="text-muted">
                  Your password has been changed. Taking you to your dashboard now.
                </p>
              </div>

              <Button className="w-full" onPress={() => navigate('/auth')}>
                Sign In Now
              </Button>
            </div>
          )}
        </Surface>

        {/* Footer Links */}
        <div className="mt-6 text-center">
          <p className="text-sm text-muted">
            Remember your password?{' '}
            <button
              onClick={() => navigate('/auth')}
              className="font-medium text-accent hover:underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  )
}
